import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AuthRedirect } from "@/components/AuthRedirect";
import { CookieConsent } from "@/components/gdpr/CookieConsent";
import { GroupGate } from "@/components/GroupGate";
import { BottomNav } from "@/components/navigation/BottomNav";
import { GlobalSOS } from "@/components/sos/GlobalSOS";
import { GlobalPitStop } from "@/components/pit-stop/GlobalPitStop";

// Utility to retry loading chunks if they fail (e.g., after a new PWA deployment deletes old chunks)
const lazyWithRetry = (componentImport: () => Promise<any>) =>
  lazy(async () => {
    try {
      const component = await componentImport();
      sessionStorage.removeItem("retry-lazy-refreshed");
      return component;
    } catch (error: any) {
      if (!sessionStorage.getItem("retry-lazy-refreshed")) {
        sessionStorage.setItem("retry-lazy-refreshed", "true");
        window.location.reload();
        // Return a promise that never resolves while reloading
        return new Promise(() => {});
      }
      throw error;
    }
  });

// Lazy load route components
const Index = lazyWithRetry(() => import("./pages/Index"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const Onboarding = lazyWithRetry(() => import("./pages/Onboarding"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const Chat = lazyWithRetry(() => import("./pages/Chat"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const Terms = lazyWithRetry(() => import("./pages/Terms"));
const Groups = lazyWithRetry(() => import("./pages/Groups"));
const GroupDetail = lazyWithRetry(() => import("./pages/GroupDetail"));
const SOSSettings = lazyWithRetry(() => import("./pages/SOSSettings"));
const Events = lazyWithRetry(() => import("./pages/Events"));
const EventDetail = lazyWithRetry(() => import("./pages/EventDetail"));
const Marketplace = lazyWithRetry(() => import("./pages/Marketplace"));
const BusinessDetail = lazyWithRetry(() => import("./pages/BusinessDetail"));
const Admin = lazyWithRetry(() => import("./pages/Admin"));
const RideMode = lazyWithRetry(() => import("./pages/RideMode"));
const RoutesPage = lazyWithRetry(() => import("./pages/RoutesPage"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner 
        position="top-center"
        toastOptions={{
          style: {
            background: "hsl(var(--card))",
            border: "1px solid hsl(var(--border))",
            color: "hsl(var(--foreground))",
          },
        }}
      />
      <BrowserRouter>
        <Suspense fallback={<div className="min-h-screen bg-background" />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={
              <AuthRedirect>
                <Auth />
              </AuthRedirect>
            } />
            <Route path="/onboarding" element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <GroupGate>
                  <Dashboard />
                </GroupGate>
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />
            <Route path="/chat" element={
              <ProtectedRoute>
                <GroupGate>
                  <Chat />
                </GroupGate>
              </ProtectedRoute>
            } />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/groups" element={
              <ProtectedRoute>
                <GroupGate>
                  <Groups />
                </GroupGate>
              </ProtectedRoute>
            } />
            <Route path="/groups/:groupId" element={
              <ProtectedRoute>
                <GroupGate>
                  <GroupDetail />
                </GroupGate>
              </ProtectedRoute>
            } />
            <Route path="/sos-settings" element={
              <ProtectedRoute>
                <GroupGate>
                  <SOSSettings />
                </GroupGate>
              </ProtectedRoute>
            } />
            <Route path="/events" element={
              <ProtectedRoute>
                <GroupGate>
                  <Events />
                </GroupGate>
              </ProtectedRoute>
            } />
            <Route path="/events/:eventId" element={
              <ProtectedRoute>
                <GroupGate>
                  <EventDetail />
                </GroupGate>
              </ProtectedRoute>
            } />
            <Route path="/marketplace" element={
              <ProtectedRoute>
                <GroupGate>
                  <Marketplace />
                </GroupGate>
              </ProtectedRoute>
            } />
            <Route path="/marketplace/:businessId" element={
              <ProtectedRoute>
                <GroupGate>
                  <BusinessDetail />
                </GroupGate>
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            } />
            <Route path="/ride" element={
              <ProtectedRoute>
                <RideMode />
              </ProtectedRoute>
            } />
            <Route path="/routes" element={
              <ProtectedRoute>
                <GroupGate>
                  <RoutesPage />
                </GroupGate>
              </ProtectedRoute>
            } />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <GlobalPitStop />
        <BottomNav />
        <GlobalSOS />
        <CookieConsent />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
